import {
    Box,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Button,
    Typography,
    Tab,
    Tabs
} from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useContext, useState } from "react";
import ComboFilters from "./SelectBoxFilter";
import repositoryImg from "../assets/repository.png";
import indicatorsImg from "../assets/indicators.png";
import downloadImg from "../assets/download.png";
import { FiltersContext } from "../store/filters-options-context";
import BarChartIcon from "@mui/icons-material/BarChart";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

export default function StepperHome({
    handleRoute,
    handleChange,
    options,
    active,
}) {
    const { valueSelected, mainCategory, setMainCategory, setValueSelected, setParams } =
        useContext(FiltersContext);
    const [activeStep, setActiveStep] = useState(0);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = (i) => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        if (i === 1) {
            setValueSelected((prevValue) => ({
                ...prevValue,
                area_code_m49: null,
                area: null,
            }));
        } else {
            setMainCategory("");
            setValueSelected((prevValue) => ({
                ...prevValue,
                domain_code: null,
            }));
            setParams((prevParams) => ({
                ...prevParams,
                domain_code: "",
            }));
        }
    };

    const steps = [
        {
            label: "Select a Country",
            description: (
                <Box sx={{ mb: 1 }}>
                    <Box>
                        Explore the detailed data and insights associated with a particular
                        country.
                    </Box>
                    <ComboFilters
                        selectOptions={options["area"]}
                        selectId="area"
                        onChange={handleChange}
                        selectLabel="Select a Country"
                        handleNext={handleNext}
                    />
                </Box>
            ),
        },
        {
            label: "Select a Product",
            description: (
                <Box sx={{ mb: 1, width: "100%" }}>
                    {/* Horizontal Tabs */}
                    <Tabs
                        value={mainCategory || false}
                        onChange={(e, newValue) => setMainCategory(newValue)}
                        orientation="horizontal"
                        variant="fullWidth"
                        sx={{
                            width: "100%",
                            borderRadius: "10px",
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            "& .MuiTab-root": {
                                flex: 1,
                                minHeight: "auto",
                                py: 2,
                                px: 1,
                                textTransform: "none",
                                fontSize: { xs: "0.65rem", sm: "0.8rem", md: "0.95rem" },
                                whiteSpace: "normal",
                                borderRight: "1px solid",
                                borderColor: "divider",
                            },
                            "& .MuiTab-root:last-of-type": {
                                borderRight: "none",
                            },
                            "& .Mui-selected": {
                                backgroundColor: "secondary.main",
                                color: "primary.contrastText",
                                fontWeight: 600,
                            },
                        }}
                    >
                        <Tab
                            icon={<BarChartIcon />}
                            iconPosition="start"
                            value="indicators"
                            label="Indicators"
                        />
                        <Tab
                            icon={<Inventory2OutlinedIcon />}
                            iconPosition="start"
                            value="repository"
                            label="Repository"
                        />
                        <Tab
                            icon={<FileDownloadIcon />}
                            iconPosition="start"
                            value="bulk"
                            label="Bulk Data"
                        />
                    </Tabs>

                    {/* Content panel below */}
                    <Box sx={{ mt: 2 }}>
                        {mainCategory === "indicators" && (
                            <Typography>
                                Visualize the <em><b>AFS Indicators</b></em>, which include charts
                                and tables for a more graphical and quantitative representation.
                            </Typography>
                        )}
                        {mainCategory === "repository" && (
                            <Typography>
                                Access the <em><b>AFS repository</b></em>, which contains a
                                collection of documents and links for data reading and reference.
                            </Typography>
                        )}
                        {mainCategory === "bulk" && (
                            <Typography>
                                Access the <em><b>AFS Bulk Data</b></em>, which contains
                                specifications to generate a CSV report file matching the requested
                                indicators within a year-range.
                            </Typography>
                        )}
                    </Box>

                    {/* Next button for stepper */}
                    <Box sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleNext}
                            disabled={!mainCategory}
                        >
                            Next
                        </Button>
                    </Box>
                </Box>
            ),
        },
        {
            label: "Explore data",
            description: (
                <Box sx={{ pb: 2 }}>
                    {mainCategory !== "bulk" && (
                        <Box>
                            Select the {mainCategory === "repository" ? "category" : "outcome"}{" "}
                            you would like to explore.
                        </Box>
                    )}
                    {mainCategory === "indicators" && (
                        <ComboFilters
                            selectOptions={options["domain_code"]}
                            selectId="domain_code"
                            onChange={handleChange}
                            selectLabel="Select an Outcome"
                        />
                    )}
                    {mainCategory === "repository" && (
                        <ComboFilters
                            selectOptions={options["repositories"]}
                            selectId="repositories"
                            onChange={handleChange}
                            selectLabel="Select a category from the repository"
                        />
                    )}
                    {mainCategory === "bulk" && (
                        <Box>
                            You have selected bulk data download. Click "Show Data" to proceed.
                        </Box>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box
            display="flex"
            sx={(theme) => ({
                my: 4,
                px: 3,
                py: 3,
                backgroundColor: theme.palette.secondary.main,
                borderRadius: "20px",
                boxShadow: 3,
                transform: {
                    xs: "none",
                    lg: activeStep > 0 ? "translateX(90px)" : "translateX(0)",
                },
                transition: "transform 0.4s ease-in-out",
                // Add mobile scrolling support
                maxHeight: {
                    xs: "calc(100vh - 140px)", // Account for header and margin
                    lg: "none"
                },
                overflowY: {
                    xs: "auto",
                    lg: "visible"
                },
            })}
        >
            <Box
                sx={{
                    width: { xs: "79vw", md: 550 },
                    position: "relative",
                    // Add mobile scrolling support for inner content
                    maxHeight: {
                        xs: "calc(100vh - 200px)", // Account for container padding
                        lg: "none"
                    },
                    overflowY: {
                        xs: "auto",
                        lg: "visible"
                    },
                }}
            >
                <Stepper
                    activeStep={activeStep}
                    orientation="vertical"
                    sx={{
                        "& .MuiStepConnector-line": {
                            minHeight: "17px",
                        },
                    }}
                >
                    {steps.map((step, index) => (
                        <Step key={step.label}>
                            <StepLabel
                                sx={{
                                    "& .MuiStepLabel-label": {
                                        fontWeight: 600,
                                        position: "relative",
                                    },
                                }}
                            >
                                {step.label}
                            </StepLabel>
                            <StepContent>
                                {step.description}
                                <Box sx={{ mb: 1 }}>
                                    <div>
                                        {index === steps.length - 1 && (
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    document
                                                        .querySelector("#root")
                                                        .scrollTo({ top: 0, behavior: "auto" });
                                                    setTimeout(() => {
                                                        handleRoute(mainCategory);
                                                    }, 300);
                                                }}
                                                disabled={!active}
                                                sx={{ mr: 1 }}
                                            >
                                                Show Data
                                            </Button>
                                        )}
                                        {index > 0 && (
                                            <Button onClick={() => handleBack(index)} sx={{ mr: 1 }}>
                                                Back
                                            </Button>
                                        )}
                                    </div>
                                </Box>
                            </StepContent>
                        </Step>
                    ))}
                </Stepper>
                {valueSelected.area && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "0",
                            left: "-225px",
                            animation: "toLeft 0.8s ease-in-out",
                            alignItems: "center",
                            flexDirection: "column",
                            display: { xs: "none", lg: "flex" },
                            minWidth: "155px",
                        }}
                    >
                        <img
                            width="125"
                            key={valueSelected.area.code}
                            srcSet={`https://flagcdn.com/w320/${valueSelected.area.code.toLowerCase()}.png 2x`}
                            src={`https://flagcdn.com/w160/${valueSelected.area.code.toLowerCase()}.png`}
                            alt=""
                            style={{ borderRadius: "5px" }}
                        />
                        <Typography
                            color="primary"
                            backgroundColor="secondary.dark"
                            justifyContent="center"
                            borderRadius="8px"
                            padding="3px 10px"
                            marginTop="5px"
                            fontSize="smaller"
                            minWidth="100%"
                            maxWidth="155px"
                            textAlign="center"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            overflow="hidden"
                        >
                            {valueSelected.area.label}
                        </Typography>
                    </Box>
                )}
                {mainCategory && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: "52%",
                            left: "-225px",
                            animation: "toLeft 0.8s ease-in-out",
                            alignItems: "center",
                            flexDirection: "column",
                            display: { xs: "none", lg: "flex" },
                            minWidth: "155px",
                        }}
                    >
                        <img
                            width="115"
                            key={mainCategory}
                            src={mainCategory === "repository" ? repositoryImg : (mainCategory === "indicators" ? indicatorsImg : downloadImg)}
                            alt=""
                        />
                        <Typography
                            color="primary"
                            backgroundColor="secondary.dark"
                            display="flex"
                            justifyContent="center"
                            borderRadius="8px"
                            padding="3px 10px"
                            marginTop="5px"
                            fontSize="smaller"
                            minWidth="100%"
                            textTransform="capitalize"
                        >
                            {mainCategory}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}